import React, { useMemo, useState } from 'react';

const ELIGIBILITY_KEY = 'polyrth.eligibility.v1';
const CHECKER_NOTICE_KEY = 'polyrth.checker.notice.v1';

function normalizeWallet(s) {
  return String(s || '').trim().toLowerCase();
}

function isValidWallet(s) {
  return /^0x[a-f0-9]{40}$/.test(normalizeWallet(s));
}

export default function Checker() {
  const [notice] = useState(() => {
    try {
      const v = sessionStorage.getItem(CHECKER_NOTICE_KEY) || '';
      if (v) sessionStorage.removeItem(CHECKER_NOTICE_KEY);
      return String(v || '');
    } catch {
      return '';
    }
  });
  const [wallet, setWallet] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const canSubmit = useMemo(() => isValidWallet(wallet) && status !== 'loading', [wallet, status]);

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    setResult(null);

    if (!isValidWallet(wallet)) {
      setStatus('error');
      setError('Enter a valid 0x wallet address.');
      return;
    }

    setStatus('loading');
    try {
      const res = await fetch('/api/check-wallet', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ wallet: normalizeWallet(wallet) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setStatus('error');
        setError(String(data?.error || 'Request failed.'));
        return;
      }
      setResult(data);
      setStatus('done');

      // Persist eligibility for this tab session (used to gate Insiders)
      try {
        sessionStorage.setItem(
          ELIGIBILITY_KEY,
          JSON.stringify({
            wallet: normalizeWallet(wallet),
            eligible: Boolean(data?.eligible),
            phase: data?.phase ?? null,
            ts: Date.now(),
          })
        );
      } catch {
        // ignore
      }
    } catch {
      setStatus('error');
      setError('Network error. Please try again.');
    }
  }

  const headline =
    status === 'done' && result?.eligible
      ? 'YOU’RE ON THE LIST'
      : status === 'done'
        ? 'NOT FOUND'
        : 'CHECK YOUR STATUS';

  const sub =
    status === 'done'
      ? result?.eligible
        ? `Phase: ${String(result?.phase || 'TBD')}`
        : 'This wallet is not eligible.'
      : 'Enter your wallet. We’ll tell you if it’s eligible.';

  return (
    <div className="min-h-screen w-full bg-black text-white overflow-x-hidden">
      {/* Background (match Home) */}
      <div className="pointer-events-none fixed inset-0">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(1200px 900px at 60% 30%, rgba(255,255,255,0.06), transparent 60%), radial-gradient(900px 700px at 20% 20%, rgba(0,16,255,0.10), transparent 55%), radial-gradient(1000px 800px at 50% 120%, rgba(255,255,255,0.05), transparent 60%)',
          }}
        />
        <div
          className="absolute -bottom-[45vh] right-[-20vw] h-[90vh] w-[90vh] rounded-full opacity-70"
          style={{
            background:
              'radial-gradient(circle at 35% 35%, #f5f5f5 0%, #d9d9d9 35%, #6b6b6b 62%, #1a1a1a 78%, rgba(0,0,0,0) 79%)',
            filter: 'grayscale(100%) contrast(105%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.14] mix-blend-overlay"
          style={{
            backgroundImage:
              'radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px), radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)',
            backgroundSize: '3px 3px, 5px 5px',
            backgroundPosition: '0 0, 2px 1px',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/85" />
      </div>

      <div className="relative mx-auto max-w-6xl px-6 pt-28 pb-20">
        <div className="text-center">
          <div className="polyrth-mono text-xs tracking-[0.35em] uppercase text-white/60">
            polyrth
          </div>
          <div className="mt-5 polyrth-display polyrth-outline text-[40px] sm:text-[64px] leading-[0.95] font-black uppercase tracking-[0.10em]">
            {headline}
          </div>
          <div className="mt-4 polyrth-mono text-sm sm:text-base leading-relaxed text-white/65">
            {sub}
          </div>
          {notice ? (
            <div className="mt-4 polyrth-mono text-sm text-white/70">{notice}</div>
          ) : null}
        </div>

        <div className="mt-12 flex justify-center">
          <div className="w-full max-w-2xl">
            <div className="rounded-2xl border border-white/12 bg-white/5 backdrop-blur-sm shadow-2xl">
              <div className="p-6 sm:p-8">
                <div className="polyrth-mono text-xs tracking-[0.28em] uppercase text-white/55">
                  Wallet address
                </div>

                <form onSubmit={onSubmit} className="mt-4">
                  <input
                    value={wallet}
                    onChange={(e) => setWallet(e.target.value)}
                    placeholder="0x…"
                    spellCheck={false}
                    autoCapitalize="none"
                    autoCorrect="off"
                    className="polyrth-mono w-full rounded-xl border border-white/15 bg-black/40 px-4 py-4 text-sm text-white placeholder:text-white/35 outline-none focus:border-white/30"
                  />

                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className={`polyrth-mono mt-4 w-full rounded-xl border px-6 py-4 text-sm font-bold tracking-[0.22em] uppercase transition ${
                      canSubmit
                        ? 'border-white/25 bg-white text-black hover:bg-white/90'
                        : 'border-white/10 bg-white/5 text-white/35 cursor-not-allowed'
                    }`}
                  >
                    {status === 'loading' ? 'Checking…' : 'Check'}
                  </button>
                </form>

                {error ? (
                  <div className="mt-4 polyrth-mono text-sm text-red-300">{error}</div>
                ) : null}

                {status === 'done' ? (
                  <div className="mt-5 polyrth-mono text-sm text-white/80">
                    {result?.eligible ? (
                      <>
                        <span className="text-white">Eligible</span>.{' '}
                        <span className="text-white/60">Phase:</span>{' '}
                        <span className="text-white font-bold">{String(result?.phase || 'TBD')}</span>
                      </>
                    ) : (
                      <>Not eligible.</>
                    )}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mt-5 text-center polyrth-mono text-xs text-white/45">
              The eligibility list is private and verified server-side.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

